/**
 * Live build progress — the ONE ordered phase list a build moves through and
 * the version-doc field the builder mirrors it into (`buildProgress`, jsonb
 * column `build_progress`, migration 0068).
 *
 * The builder reports a `progress` trace step at the START of every phase and
 * sub-step (writing round r of R on model M, gate k by name, safety review,
 * diagnosis, quality pass p of P, art join, rendered audit, publishing); the
 * worker's progress tap mirrors the latest one onto the version doc, and the
 * studio's Iterate tab renders the phase tracker + "step n of N" counter from
 * THIS list. `queued` and `live` are never reported — they are derived from
 * the version's `buildStatus`, which stays the only truth for terminal states.
 */
export const BUILD_PHASES = [
  'queued',
  'setup',
  'writing',
  'gates',
  'review',
  'diagnosis',
  'quality',
  'art',
  'audit',
  'publishing',
  'live',
] as const;

export type BuildPhase = (typeof BUILD_PHASES)[number];

/** A phase the builder itself reports (the status-derived endpoints excluded). */
export type ReportedBuildPhase = Exclude<BuildPhase, 'queued' | 'live'>;

/** What the builder reports at a phase/step start (the tap stamps the rest). */
export interface BuildProgressEvent {
  phase: ReportedBuildPhase;
  /** Safe counts/timestamps only. Heartbeats never populate provider activity. */
  activity?: {
    requestId: string; attempt: number; requestedModel: string; actualModel?: string;
    provider?: string; startedAt: number; providerActivityAt?: number;
    firstActivityAt?: number; firstTextAt?: number; visibleChars: number;
    fallbackReason?: 'provider-unavailable' | 'credit-refusal-circuit-open';
  };
  /** Checked construction stage within one experimental build. */
  constructionStage?: 'core' | 'complete';
  /** LLM edit round in progress (1-based) + the round budget — `writing`,
   *  `gates`, `review`, `diagnosis`. */
  round?: number;
  rounds?: number;
  /** Quality-loop pass (1-based) + the pass budget: the pass being scored
   *  (`quality`), or the targeted pass a `writing`/`gates` step belongs to.
   *  Absent on the first pass and on plain builds. */
  pass?: number;
  passes?: number;
  /** The gate running now (`gates`): its id (gates/run.ts emit name) and
   *  1-based ordinal in this round's battery. */
  gate?: string;
  gateIndex?: number;
  /** The model writing the code (`writing`). */
  model?: string;
}

/** The version doc's live progress pointer (`buildProgress`). */
export interface BuildProgress extends BuildProgressEvent {
  /** Epoch ms when this phase/step began (the studio shows elapsed time). */
  at: number;
  /** Every distinct phase this build has reached so far, in first-seen order
   *  — lets the tracker mark an earlier phase the build never entered
   *  (diagnosis, quality, art) as skipped rather than done. */
  reached: ReportedBuildPhase[];
}
